// Rule combinators — a direct port of
// api/domain/assessment/config/eligibility/utils.go. The atoms return null
// ("unknown, try the next rule") whenever the fact is absent or does not equal
// the required value; this null-propagation is what produces the three-state
// result at the top level. Verified against source 2026-07 (see engine/rules.ts).

import type { Checker, Facts, RuleOutcome } from '@/benefits-flow/engine/types';

// Production hardcodes Confidence 1.0 in every EligibilityResult it constructs.
const CONFIDENCE = 1.0;

const eligible = (): RuleOutcome => ({
  eligible: true,
  confidence: CONFIDENCE,
});
const ineligible = (): RuleOutcome => ({
  eligible: false,
  confidence: CONFIDENCE,
});

// matchFn(required, result, key): non-nil only when the fact is present and its
// boolean value equals `required`; then returns `result` as the verdict.
const matchFn =
  (required: boolean, result: boolean, key: string): Checker =>
  (facts: Facts) => {
    const f = facts[key];
    if (f === undefined) return null;
    if (Boolean(f.value) !== required) return null;
    return result ? eligible() : ineligible();
  };

// anyMatchFn: the first key present-and-equal-to-required wins; else nil.
const anyMatchFn =
  (required: boolean, result: boolean, keys: string[]): Checker =>
  (facts: Facts) => {
    for (const key of keys) {
      const f = facts[key];
      if (f !== undefined && Boolean(f.value) === required) {
        return result ? eligible() : ineligible();
      }
    }
    return null;
  };

// allMatchFn: every key must be present and equal to required; any missing or
// differing key → nil. This is why EligibleIfAllMetricsTrue yields "unknown"
// (not ineligible) on a half-finished screener.
const allMatchFn =
  (required: boolean, result: boolean, keys: string[]): Checker =>
  (facts: Facts) => {
    for (const key of keys) {
      const f = facts[key];
      if (f === undefined || Boolean(f.value) !== required) return null;
    }
    return result ? eligible() : ineligible();
  };

export const EligibleIfMetricTrue = (key: string): Checker =>
  matchFn(true, true, key);

export const IneligibleIfMetricTrue = (key: string): Checker =>
  matchFn(true, false, key);

export const IneligibleIfMetricFalse = (key: string): Checker =>
  matchFn(false, false, key);

export const EligibleIfAnyMetricTrue = (...keys: string[]): Checker =>
  anyMatchFn(true, true, keys);

export const EligibleIfAllMetricsTrue = (...keys: string[]): Checker =>
  allMatchFn(true, true, keys);

export const IneligibleIfAllMetricsFalse = (...keys: string[]): Checker =>
  allMatchFn(false, false, keys);

// And: every inner checker must be non-nil AND eligible, else nil. It can only
// ever emit an eligible verdict — never ineligible (matches source: it returns
// nil the moment any inner result is nil or not eligible).
export const And =
  (...checkers: Checker[]): Checker =>
  (facts: Facts) => {
    for (const c of checkers) {
      const r = c(facts);
      if (r === null || !r.eligible) return null;
    }
    return eligible();
  };

// Or: the first inner checker that is non-nil AND eligible wins; else nil.
export const Or =
  (...checkers: Checker[]): Checker =>
  (facts: Facts) => {
    for (const c of checkers) {
      const r = c(facts);
      if (r !== null && r.eligible) return r;
    }
    return null;
  };
